'use client';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, Users, Rocket, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import Link from "next/link";

export default function OffersPage() {
    return (
        <>
            <section className="bg-secondary pt-12">
                <div className="container mx-auto px-4 text-center">
                    <Badge variant="accent" className="mb-4 py-1 px-4 text-sm font-bold uppercase tracking-widest">Limited Time</Badge>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-headline text-foreground">Exclusive Learning Offers</h1>
                    <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                        Empower your child's future with our special enrollment and referral programs. 
                    </p>
                </div>
            </section>

            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-stretch">
                        {/* Website Launch Offer */}
                        <Card className="relative overflow-hidden border-2 border-primary/20 shadow-2xl flex flex-col">
                            <div className="absolute top-0 right-0 bg-primary text-white px-6 py-2 rounded-bl-2xl font-bold z-10">
                                HOT DEAL
                            </div>
                            <CardHeader className="bg-primary/5 pb-8">
                                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg mb-6">
                                    <Rocket className="w-10 h-10 text-primary" />
                                </div>
                                <CardTitle className="text-3xl font-headline font-bold">Grand Website Launch Offer</CardTitle>
                                <CardDescription className="text-lg font-medium text-primary">Valid from 3rd April to 30th April 2026</CardDescription>
                            </CardHeader>
                            <CardContent className="pt-8 flex-grow">
                                <p className="text-muted-foreground mb-6">To celebrate the launch of our new digital home, we are offering an exclusive discount for all new enrollments during the month of April!</p>
                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                                        <span className="font-medium">A special discount on all course enrollments</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                                        <span className="font-medium">5% discount on the most used or most bought courses</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                                        <span className="font-medium">A free demo class for any 1 course</span>
                                    </li>
                                </ul>
                                <Button asChild variant="accent" size="lg" className="w-full rounded-full py-7 text-lg font-bold">
                                    <Link href="/book-demo">Claim Launch Offer Now</Link>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Referral Program */}
                        <Card className="relative overflow-hidden border-2 border-accent/20 shadow-2xl flex flex-col">
                            <div className="absolute top-0 right-0 bg-accent text-accent-foreground px-6 py-2 rounded-bl-2xl font-bold z-10">
                                BEST VALUE
                            </div>
                            <CardHeader className="bg-accent/5 pb-8">
                                <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg mb-6">
                                    <Users className="w-10 h-10 text-accent" />
                                </div>
                                <CardTitle className="text-3xl font-headline font-bold">Refer & Win Big</CardTitle>
                                <CardDescription className="text-lg font-medium text-accent">Help other parents and save on your next level!</CardDescription>
                            </CardHeader>
                            <CardContent className="pt-8 flex-grow">
                                <p className="text-muted-foreground mb-8">Invite your friends and family to join Upbeat Brain Game. The more students you refer, the more you save on your child's next level fees.</p>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                                    <div className="bg-white border rounded-xl p-4 text-center">
                                        <div className="text-2xl font-bold text-primary">10% OFF</div>
                                        <div className="text-sm text-muted-foreground">1 Referral</div>
                                    </div>
                                    <div className="bg-white border rounded-xl p-4 text-center">
                                        <div className="text-2xl font-bold text-primary">20% OFF</div>
                                        <div className="text-sm text-muted-foreground">2 Referrals</div>
                                    </div>
                                    <div className="bg-white border rounded-xl p-4 text-center">
                                        <div className="text-2xl font-bold text-primary">30% OFF</div>
                                        <div className="text-sm text-muted-foreground">3 Referrals</div>
                                    </div>
                                </div>

                                <div className="bg-primary/10 border-2 border-primary/30 rounded-2xl p-6 mb-8 relative">
                                    <div className="absolute -top-3 left-6 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                        <Sparkles className="w-3 h-3" /> BULK OFFER
                                    </div>
                                    <h4 className="text-xl font-bold font-headline mb-2">The Ultimate Free Level</h4>
                                    <p className="text-sm text-muted-foreground">Refer <span className="font-bold text-primary">10 Students</span> and get <span className="font-bold text-primary">100% OFF</span> on your child's next level fees! The next level will be completely FREE for your child.</p>
                                </div>

                                <Button asChild variant="outline" size="lg" className="w-full rounded-full py-7 text-lg font-bold border-2 hover:bg-accent hover:text-accent-foreground">
                                    <Link href="/contact">Inquire About Referrals</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="mt-20 max-w-3xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 bg-secondary/50 p-3 px-6 rounded-full mb-6">
                            <Gift className="w-5 h-5 text-primary" />
                            <span className="text-sm font-semibold italic text-muted-foreground">More special offers coming soon for festive occasions!</span>
                        </div>
                        <h3 className="text-2xl font-bold font-headline mb-4">Have Questions About Our Offers?</h3>
                        <p className="text-muted-foreground mb-8">Our counseling team is here to help you choose the best plan for your child's success.</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button asChild variant="link" className="text-primary font-bold">
                                <Link href="/contact">Talk to a Counselor <ArrowRight className="w-4 h-4 ml-1" /></Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
